const DEFAULT_BRAND = 'EngageNet';
const DEFAULT_DESCRIPTION = [
  'EngageNet is a community and workstream platform for portals, events,',
  'memberships, marketplace activity, reporting, and collaboration.',
].join(' ');

const PUBLIC_ROUTE_SEO = {
  landing: {
    title: 'EngageNet',
    description: [
      'Build and manage thriving digital communities with portals, events,',
      'collaboration, memberships, and marketplace tools.',
    ].join(' '),
  },
  login_root: {
    title: 'Sign In',
    description: 'Sign in to your EngageNet workspace.',
    noindex: true,
  },
  register: {
    title: 'Create Account',
    description: 'Create an account to join your EngageNet community.',
  },
  Marketplace: {
    title: 'Marketplace',
    description: [
      'Browse marketplace products, services, and business opportunities',
      'on EngageNet.',
    ].join(' '),
  },
  events: {
    title: 'Events',
    description: [
      'Discover upcoming events, programmes, and community activities',
      'on EngageNet.',
    ].join(' '),
  },
  view_events: {
    title: 'Events',
    description: 'View published events and community activities on EngageNet.',
  },
  view_event: {
    title: 'Event Details',
    description: 'View event details, resources, RSVP options, and ticket information.',
  },
  view_business_profiles: {
    title: 'Business Profiles',
    description: [
      'Explore business profiles and organisations in the EngageNet',
      'network.',
    ].join(' '),
  },
  view_personal_profiles: {
    title: 'Profiles',
    description: 'Explore public member and professional profiles on EngageNet.',
  },
  publish_portal: {
    title: 'Portal',
    description: 'View a published portal and its community content.',
  },
  view_full_report: {
    title: 'Report',
    description: 'Read a published report from an EngageNet portal.',
  },
  knowledgehub: {
    title: 'Knowledge Hub',
    description: 'Access learning resources, knowledge hub content, and programme information.',
  },
  assessment_tools: {
    title: 'Assessment Tools',
    description: 'Access public assessment tools and resources.',
  },
  courses: {
    title: 'Courses',
    description: 'Browse available courses and learning resources.',
  },
  youth_empowerment: {
    title: 'Youth Empowerment',
    description: 'Explore youth empowerment resources and programme dashboards.',
  },
  pwd: {
    title: 'Persons With Disabilities',
    description: 'Access resources and dashboards for persons with disabilities.',
  },
  gbv: {
    title: 'Gender-Based Violence Resources',
    description: [
      'Access gender-based violence learning resources, dashboards,',
      'and support content.',
    ].join(' '),
  },
  insights: {
    title: 'Insights',
    description: 'Explore community insights, knowledge dashboards, and programme data.',
  },
  view_incidents: {
    title: 'Incident Reports',
    description: 'View public incident reports and reporting information.',
  },
  incident_reporting: {
    title: 'Report an Incident',
    description: 'Submit an incident report securely through EngageNet.',
  },
  ProductView: {
    title: 'Product',
    description: 'View marketplace product details on EngageNet.',
  },
  page_not_found: {
    title: 'Page Not Found',
    description: 'The page you are looking for could not be found.',
    noindex: true,
  },
};

function getBrandName() {
  return document.documentElement.getAttribute('data-client') || DEFAULT_BRAND;
}

function getBaseUrl() {
  const baseUrl = process.env.VUE_APP_FRONTEND_URL || window.location.origin;
  return baseUrl.replace(/\/$/, '');
}

function getCanonicalUrl(route) {
  const path = route.path || window.location.pathname;
  return `${getBaseUrl()}${path === '/' ? '/' : path.replace(/\/$/, '')}`;
}

function ensureMeta(selector, createAttributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
    document.head.appendChild(element);
  }
  return element;
}

function ensureCanonical() {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  return element;
}

function setMeta(name, content) {
  ensureMeta(`meta[name="${name}"]`, { name }).setAttribute('content', content);
}

function setProperty(property, content) {
  ensureMeta(`meta[property="${property}"]`, { property }).setAttribute('content', content);
}

function getRouteSeo(route) {
  const routeSeo = PUBLIC_ROUTE_SEO[route.name] || {};
  const metaSeo = route.meta && route.meta.seo ? route.meta.seo : {};
  return {
    ...routeSeo,
    ...metaSeo,
  };
}

export function applyRouteSeo(route) {
  const brand = getBrandName();
  const seo = getRouteSeo(route);
  const routeTitle = seo.title || brand;
  const title = routeTitle === brand ? brand : `${routeTitle} | ${brand}`;
  const description = seo.description || DEFAULT_DESCRIPTION;
  const canonicalUrl = getCanonicalUrl(route);
  const isPrivate = route.matched.some(
    record => record.meta && record.meta.requiresAuth,
  );
  const shouldNoindex = seo.noindex || isPrivate;

  document.title = title;
  ensureCanonical().setAttribute('href', canonicalUrl);

  setMeta('description', description);
  setMeta('robots', shouldNoindex ? 'noindex,nofollow' : 'index,follow');
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);

  setProperty('og:title', title);
  setProperty('og:description', description);
  setProperty('og:url', canonicalUrl);
  setProperty('og:type', 'website');
  setProperty('og:site_name', brand);
}
